/**
 * useNetobsTraffic — React hook that reads NetworkTracer's recentTraffic
 * ring buffer + computes per-second buckets for the last `windowSecs`
 * seconds. Powers NetobsPacketSparkline (NO-3).
 *
 * Why we don't reuse `tracer.currentThroughput`: that's recomputed every
 * 2s and counts INBOUND messages only (no bytes, no outbound, no
 * sub-second resolution). The sparkline needs 30 buckets × 1s of both
 * directions × bytes for a useful visual.
 *
 * Polling cadence: 250ms — matches LatencyOverlay + multiplayerDiagnostics.
 * The walk over `recentTraffic` (ring of 300 events) is cheap.
 */

import { useEffect, useState } from 'react';
import { isPopoutMode } from '../../systems/netobs/popout/popoutMode';
import { popoutShim } from '../../systems/netobs/popout/popoutShimState';

export interface NetobsTrafficBucket {
  /** Bucket end-time (ms epoch). */
  tEnd: number;
  bytesIn: number;
  bytesOut: number;
  msgsIn: number;
  msgsOut: number;
}

export interface NetobsTrafficSnapshot {
  buckets: NetobsTrafficBucket[];
  /** Sum across all buckets (last `windowSecs` seconds). */
  totalBytesIn: number;
  totalBytesOut: number;
  totalMsgsIn: number;
  totalMsgsOut: number;
  /** Most recent 1s rate, in bytes/sec. */
  bytesInPerSec: number;
  bytesOutPerSec: number;
  msgsInPerSec: number;
  msgsOutPerSec: number;
  /** True iff the tracer is bound + producing data. */
  ready: boolean;
}

const EMPTY: NetobsTrafficSnapshot = {
  buckets: [],
  totalBytesIn: 0,
  totalBytesOut: 0,
  totalMsgsIn: 0,
  totalMsgsOut: 0,
  bytesInPerSec: 0,
  bytesOutPerSec: 0,
  msgsInPerSec: 0,
  msgsOutPerSec: 0,
  ready: false,
};

const POLL_INTERVAL_MS = 250;
const DEFAULT_WINDOW_SECS = 30;

interface TrafficEvent {
  timestamp: string;     // ISO
  direction: string;     // 'client->server' | 'server->client'
  type: string;
  bytes: number;
}

function readTracer(): TrafficEvent[] | null {
  // Popout mode: read from the BroadcastChannel-fed shim instead of
  // the live NetworkTracer (which doesn't exist — popout has no
  // Colyseus connection).
  if (isPopoutMode()) {
    const t = popoutShim.getTraffic();
    return t ? (t.recentTraffic as TrafficEvent[]) : null;
  }
  const tracer = (globalThis as any).nm?.tracer;
  if (!tracer || !Array.isArray(tracer.recentTraffic)) return null;
  return tracer.recentTraffic as TrafficEvent[];
}

function computeSnapshot(events: TrafficEvent[], windowSecs: number, now: number): NetobsTrafficSnapshot {
  // Build N buckets: bucket[i] covers (now - (windowSecs - i)*1000, now - (windowSecs - i - 1)*1000]
  const buckets: NetobsTrafficBucket[] = [];
  for (let i = 0; i < windowSecs; i++) {
    buckets.push({
      tEnd: now - (windowSecs - i - 1) * 1000,
      bytesIn: 0,
      bytesOut: 0,
      msgsIn: 0,
      msgsOut: 0,
    });
  }

  let totalBytesIn = 0;
  let totalBytesOut = 0;
  let totalMsgsIn = 0;
  let totalMsgsOut = 0;

  for (const ev of events) {
    const ts = Date.parse(ev.timestamp);
    if (!Number.isFinite(ts)) continue;
    const ageMs = now - ts;
    if (ageMs < 0 || ageMs >= windowSecs * 1000) continue;
    // bucket index 0 = oldest, windowSecs-1 = newest
    const bucketIdx = windowSecs - 1 - Math.floor(ageMs / 1000);
    if (bucketIdx < 0 || bucketIdx >= windowSecs) continue;
    const b = buckets[bucketIdx];
    const isOutbound = ev.direction === 'client->server';
    if (isOutbound) {
      b.bytesOut += ev.bytes;
      b.msgsOut += 1;
      totalBytesOut += ev.bytes;
      totalMsgsOut += 1;
    } else {
      b.bytesIn += ev.bytes;
      b.msgsIn += 1;
      totalBytesIn += ev.bytes;
      totalMsgsIn += 1;
    }
  }

  // Last 1s = newest bucket
  const last = buckets[windowSecs - 1];
  return {
    buckets,
    totalBytesIn,
    totalBytesOut,
    totalMsgsIn,
    totalMsgsOut,
    bytesInPerSec: last.bytesIn,
    bytesOutPerSec: last.bytesOut,
    msgsInPerSec: last.msgsIn,
    msgsOutPerSec: last.msgsOut,
    ready: true,
  };
}

export function useNetobsTraffic(windowSecs: number = DEFAULT_WINDOW_SECS): NetobsTrafficSnapshot {
  const [snap, setSnap] = useState<NetobsTrafficSnapshot>(EMPTY);

  useEffect(() => {
    const tick = () => {
      const events = readTracer();
      if (!events || events.length === 0) {
        // Don't churn state if nothing to show
        setSnap((prev) => (prev.ready ? EMPTY : prev));
        return;
      }
      setSnap(computeSnapshot(events, windowSecs, Date.now()));
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [windowSecs]);

  return snap;
}